import {
  EVENT_SCOPE,
  GLOBAL_EVENTS,
  GLOBAL_EVENT_TYPES,
  NATIVE_TRIGGERS,
  ProjectInteraction,
  TRIGGER_SCOPE,
} from '../types';

/**
 * Checks for interaction trigger DOM nodes
 */
function hasPointerEvent(interaction: Partial<ProjectInteraction>): boolean {
  const triggerMatch = NATIVE_TRIGGERS.find(
    (trigger) => trigger.value === interaction.trigger?.type,
  );

  return triggerMatch?.scope === TRIGGER_SCOPE.POINTER_EVENT;
}

function hasLink(interaction: Partial<ProjectInteraction>): boolean {
  const eventMatch = GLOBAL_EVENTS.find((event) => event.value === interaction.event?.type);

  return eventMatch?.scope === EVENT_SCOPE.EXTERNAL || eventMatch?.scope === EVENT_SCOPE.PAGE;
}

export function getInteractionsNode(
  interactions: ProjectInteraction[],
  defaultTag?: string,
): [string, { [attr: string]: any }] {
  if (!interactions) return [defaultTag || 'div', {}];

  const isButton = interactions?.some(hasPointerEvent);
  const isLink = interactions?.some(hasLink);

  // For links, we also need to get the target
  if (isLink) {
    const linkEvent = interactions?.find((i) => hasLink(i))?.event;
    const args = (linkEvent?.args || {}) as any;

    // Handle phone links
    if (linkEvent?.type === GLOBAL_EVENT_TYPES.CALL_PHONE) {
      return ['a', { href: `tel:${args.number}` }];
    }

    // Handle sms
    if (linkEvent?.type === GLOBAL_EVENT_TYPES.SEND_SMS) {
      return ['a', { href: `sms:${args.number}` }];
    }

    // Handle email
    if (linkEvent?.type === GLOBAL_EVENT_TYPES.SEND_EMAIL) {
      return [
        'a',
        { href: `mailto:${args.email}${args.subject ? `?subject=${args.subject}` : ''}` },
      ];
    }

    // Handle file download
    if (linkEvent?.type === GLOBAL_EVENT_TYPES.DOWNLOAD_FILE) {
      const linkArgs = { href: args.file?.url } as { [attr: string]: any };

      if (args.fileName) linkArgs.download = args.fileName;
      if (args?.action === 'newTab') linkArgs.target = '_blank';
      if (args?.action === 'newTab') linkArgs.rel = 'noopener noreferrer';
      return ['a', linkArgs];
    }

    // Handle external links
    if (linkEvent?.type === GLOBAL_EVENT_TYPES.OPEN_URL) {
      const linkArgs = { href: args.url, target: args.newTab ? '_blank' : '_self' } as {
        [attr: string]: any;
      };

      if (args.noFollow) linkArgs.rel = 'nofollow';
      if (args.sponsored) linkArgs.rel = 'sponsored';
      return ['a', linkArgs];
    }

    return ['a', { href: '#' }];
  }

  if (defaultTag) return [defaultTag, {}];
  if (isButton) return ['button', {}];
  return ['div', {}];
}
